import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Attachment } from '../../../core/models/freight-document/Attachment';
import { AttachmentService } from '../../../core/services/attachment.service';
import { FileUtils } from '../../../core/utils/FileUtils';
import { AttachmentType } from '../../../core/models/freight-document/AttachmentType';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FreightDocumentService } from '../../../core/services/freight-document.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { saveAs } from 'file-saver';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { switchMap } from 'rxjs';
import { Questionnaire } from '../../../core/models/questionnaire';
import { QuestionnaireResultPreviewModalComponent } from '../questionnaire-result-preview-modal/questionnaire-result-preview-modal.component';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { QuestionnaireService } from '../../../core/services/questionnaire.service'
import { LanguageService } from '../../../core/services/language.service'
import { PermissionsUtils } from '../../../core/utils/PermissionsUtils'

@Component({
  selector: 'app-attachments-preview',
  template: `
    <ng-container *ngFor="let attachment of attachments | filteredAttachments : attachmentTypes.SIGNONGLASS : attachmentTypes.ANNEXED_DOCUMENT">
      <ng-container *ngTemplateOutlet="attachmentRow; context: { $implicit: attachment }"></ng-container>
    </ng-container>

    <ng-container *ngFor="let group of attachments | annexedDocumentGroups; let i = index">
      <div class="text-muted small mt-2">{{ 'transportOperations.fds.create.documents.annexedDocument' | transloco }} {{ i + 1 }}</div>
      <ng-container *ngFor="let attachment of group">
        <ng-container *ngTemplateOutlet="attachmentRow; context: { $implicit: attachment }"></ng-container>
      </ng-container>
    </ng-container>

    <ng-template #attachmentRow let-attachment>
      <div class="d-flex justify-content-between align-items-center border-bottom">
        <ng-container *ngIf="canViewAttachment(attachment) else readonlyAttachment">
          <span class="btn-link text-truncate cursor-pointer" (click)="open(attachment)">
            {{ attachment | attachmentDisplayName }}
          </span>
          <div class="d-inline-flex">
            <button
              *ngIf="canDelete(attachment)"
              class="button-icon" (click)="deleteAttachment(attachment.attachmentId)">
              <span class="icon icon-delete-active"></span>
            </button>
            <button *ngIf="attachment.type === attachmentTypes.QUESTIONNAIRE" class="button-icon text-primary"
                    (click)="previewQuestionnaire(attachment)">
              <fa-icon [icon]="previewIcon"></fa-icon>
            </button>
            <button class="button-icon" (click)="download(attachment)">
              <span class="icon icon-download"></span>
            </button>
          </div>
        </ng-container>
        <ng-template #readonlyAttachment>
          <span class="text-truncate">
            {{ attachment | attachmentDisplayName }}
          </span>
          <div class="d-inline-flex">
              <span class="icon icon-info-light"
                    ngbTooltip="{{ 'general.sealedDocumentTooltip' | transloco }}"
                    tooltipClass="bg-primary-light">
            </span>
          </div>
        </ng-template>
      </div>
    </ng-template>
    <div class="mt-3" *ngIf="!noActions">
      <input [hidden]="true" type="file" #filepicker (change)="addAttachment(filepicker?.files)" [accept]="supportedMimeTypes | join" />
      <button *ngIf="!readonly" class="button-primary me-2" (click)="filepicker.click()">
        {{ 'action.addAttachment' | transloco }}
      </button>
      <button class="button-primary ms-2" (click)="downloadAll()">
        {{ 'action.downloadZip' | transloco }}
      </button>
    </div>
    <app-loader *ngIf="!readonly" [loading]="freightDocumentService.loading$ | async"></app-loader>
  `,
  providers: [{ provide: 'windowObject', useFactory: () => window }]
})
export class AttachmentsPreviewComponent implements OnInit {
  previewIcon = faEye;

  @ViewChild('filepicker') filepicker: ElementRef;
  @Input() attachments: Attachment[];
  @Input() freightDocumentId: string;
  @Input() transportId: string;
  @Input() noActions = false;
  @Input() readonly = false;
  @Input() canViewSealed = false;

  @Output() selectedAttachment = new EventEmitter<Attachment>();
  @Output() chooseAttachment = new EventEmitter<Attachment>();
  @Output() removeAttachment = new EventEmitter<string>();

  supportedMimeTypes: string[] = FileUtils.getSupportedMimeTypes();
  attachmentTypes = AttachmentType;
  deletableCreatorIds: string[] = [];

  constructor(
    @Inject('windowObject') private readonly window: Window,
    private readonly attachmentService: AttachmentService,
    private readonly questionnaireService: QuestionnaireService,
    private readonly languageService: LanguageService,
    private readonly analyticsService: AnalyticsService,
    readonly freightDocumentService: FreightDocumentService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly currentUserService: CurrentUserService,
    private readonly modalService: NgbModal
  ) {
  }

  ngOnInit(): void {
    this.currentUserService.get().subscribe(u => {
      this.deletableCreatorIds = [u.accountId, PermissionsUtils.companyAccountId(u)];
    });
  }

  canDelete(attachment: Attachment): boolean {
    return !this.readonly
      && this.removeAttachment.observed
      && attachment.type === AttachmentType.SUPPLEMENT
      && attachment.createdBy != null
      && this.deletableCreatorIds.includes(attachment.createdBy.toString());
  }

  download(attachment: Attachment): void {
    this.loadAttachment(attachment).subscribe((blob: Blob) => {
      saveAs(blob, this.getFileName(attachment));
    });
  }

  open(attachment: Attachment): void {
    this.loadAttachment(attachment).subscribe((blob: Blob) => {
      if (this.breakpointObserver.isMatched(Breakpoints.Web)) {
        this.window.open(URL.createObjectURL(blob));
      } else {
        saveAs(blob, this.getFileName(attachment));
      }
    });
  }

  private loadAttachment(attachment: Attachment) {
    if (attachment.type == AttachmentType.QUESTIONNAIRE) {
      return this.questionnaireService.getPdfByAttachmentId(attachment.attachmentId, this.languageService.getSessionLanguage());
    }

    return this.transportId ? this.attachmentService.getTransportAttachment(this.transportId, attachment.attachmentId) : this.attachmentService.get(attachment.attachmentId, attachment.mimeType);
  }

  // a questionnaire is stored as the json result, but downloaded as the pdf rendered from it
  private getFileName(attachment: Attachment): string {
    return attachment.type === AttachmentType.QUESTIONNAIRE ? `${attachment.name}.pdf` : attachment.originalFileName;
  }

  addAttachment(files: FileList): void {
    FileUtils.fileToAttachment(files.item(0), AttachmentType.SUPPLEMENT).subscribe((attachment) => {
      this.chooseAttachment.next(attachment);
      this.filepicker.nativeElement.value = '';
    });
  }

  deleteAttachment(attachmentId: string): void {
    this.removeAttachment.emit(attachmentId);
  }

  canViewAttachment(attachment: Attachment) {
    return !attachment?.sealed || this.canViewSealed;
  }

  downloadAll() {
    this.freightDocumentService.downloadZip(this.freightDocumentId).subscribe((data) => {
      saveAs(data.body, this.parseFileName(data.headers.get('content-disposition')));
      this.analyticsService.onDownloadAttachmentsZip(AttachmentsPreviewComponent.name);
    });
  }

  private parseFileName(contentDispositionHeader: string) {
    return contentDispositionHeader.split(';')[1].split('=')[1].replace(/"/g, '');
  }

  previewQuestionnaire(attachment: Attachment): void {
    this.attachmentService.get(attachment.attachmentId, attachment.mimeType)
      .pipe(
        switchMap((blob) => FileUtils.blobToText(blob))
      )
      .subscribe({
        next: (json) => {
          const questionnaire = JSON.parse(json) as Questionnaire;
          const modalRef = this.modalService.open(QuestionnaireResultPreviewModalComponent, { size: 'lg' });
          modalRef.componentInstance.questionnaire = questionnaire;
          modalRef.componentInstance.completedBy = attachment.createdBy;
        }
      });
  }
}
